import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_ICON_URL } from "@/lib/assets";
import { useLanguage } from "@/i18n/LanguageContext";

const SEEN_KEY = "mld-prelude-seen";

const EASE = [0.22, 1, 0.36, 1];
const WRAP_INIT = { opacity: 1 };
const WRAP_EXIT = { opacity: 0, transition: { duration: 1, ease: EASE } };
const LOGO_INIT = { opacity: 0, y: 8 };
const RISE_SHOW = { opacity: 1, y: 0 };
const LOGO_T = { duration: 0.9, ease: "easeOut" };
const LINE_INIT = { opacity: 0, y: 24 };
const LINE_T = { duration: 1.1, delay: 0.4, ease: EASE };
const BAR_INIT = { scaleX: 0 };
const BAR_SHOW = { scaleX: 1 };
const BAR_T = { duration: 1, delay: 1.1, ease: EASE };
const FADE_INIT = { opacity: 0 };
const FADE_SHOW = { opacity: 1 };
const SUB_T = { duration: 1, delay: 1.5 };

export const Prelude = () => {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);

  const dismiss = () => {
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[prelude]", e);
    }
    document.body.style.overflow = "";
    setShow(false);
  };

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const skip =
      new URLSearchParams(window.location.search).get("nointro") === "1";
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN_KEY) === "1";
    } catch (e) {
      seen = false;
    }
    if (reduce || seen || skip) return;
    setShow(true);
    document.body.style.overflow = "hidden";
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[prelude]", e);
    }
    const id = setTimeout(dismiss, 7000);
    return () => {
      clearTimeout(id);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="prelude"
          onClick={dismiss}
          initial={WRAP_INIT}
          exit={WRAP_EXIT}
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[#0A1420] cursor-pointer px-6"
        >
          <div className="grain-overlay" />
          <motion.img
            src={LOGO_ICON_URL}
            alt=""
            aria-hidden="true"
            initial={LOGO_INIT}
            animate={RISE_SHOW}
            transition={LOGO_T}
            className="h-16 w-16 sm:h-20 sm:w-20 object-contain mb-10 drop-shadow-[0_4px_30px_rgba(212,175,55,0.35)]"
          />
          <motion.p
            initial={LINE_INIT}
            animate={RISE_SHOW}
            transition={LINE_T}
            className="font-serif-lux italic text-4xl sm:text-6xl text-white text-center leading-tight"
          >
            {t("prelude.line")}
          </motion.p>
          <motion.span
            initial={BAR_INIT}
            animate={BAR_SHOW}
            transition={BAR_T}
            className="my-8 block h-px w-40 sm:w-56 origin-center bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"
          />
          <motion.p
            initial={FADE_INIT}
            animate={FADE_SHOW}
            transition={SUB_T}
            className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs sm:text-sm text-center"
          >
            {t("prelude.sub")}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
